/**
 * The caller's identity for DDS discovery reads (OPUS-92).
 *
 * DDS filters the competition list by the org's competition allowlist and
 * DENIES BY DEFAULT: a request without org_id answers an empty list, which
 * blanked the PLS competition pickers in Scene Controls and Session management.
 * CaptionDetailPage already sends {org_id, token}; this is the same identity
 * for the catalogue adapters.
 *
 * Which org: the one selected in the sidebar (SessionContext persists it as
 * `selectedOrgId`) — for a super_admin that is the org being operated —
 * falling back to the signed-in user's own organization from `userData`.
 *
 * DDS strips both fields before any DWS query is built, so neither can act as a
 * row filter.
 */
import authService from "../auth.service";

function readSelectedOrgId() {
  try {
    return localStorage.getItem("selectedOrgId") || null;
  } catch {
    return null;
  }
}

// userData is the full user record after a password login ({ data: { organization_id:
// { _id, name } } }) but only a thin summary after a handoff whose follow-up fetch
// failed ({ data: { orgId } }) — see AuthContext.
function readUserOrgId() {
  try {
    const data = JSON.parse(localStorage.getItem("userData") || "null")?.data;
    const org = data?.organization_id;
    return (org && typeof org === "object" ? org._id : org) || data?.orgId || null;
  } catch {
    return null;
  }
}

export function getDdsIdentityContext() {
  const token = authService.getAccessToken();
  const orgId = readSelectedOrgId() || readUserOrgId();
  return {
    ...(token ? { token: `Bearer ${token}` } : {}),
    ...(orgId ? { org_id: orgId } : {}),
  };
}

/**
 * The same identity, with the access token refreshed first when it is about to
 * expire. DDS checks the org's allowlist with this token and, when that check
 * fails, answers an EMPTY competition list instead of an error — so an expired
 * token silently blanked the pickers until the page was reloaded.
 * @returns {Promise<{token?: string, org_id?: string}>}
 */
export async function getFreshDdsIdentityContext() {
  await authService.ensureFreshAccessToken();
  return getDdsIdentityContext();
}
